/**
 * Contrato do webhook de **qualified lead** da Lais/Lastro.
 *
 * Fonte: documentação oficial entregue pela equipe da Lais (reunião de 19/08/2026),
 * transcrita em `docs/api/07-lais-integration.md`. Nada aqui é suposição — cada
 * campo abaixo aparece na documentação, com a mesma obrigatoriedade.
 *
 * ## Três decisões de projeto que governam este arquivo
 *
 * ### 1. O schema é tolerante, não estrito
 *
 * A documentação da Lais é explícita: *"some fields from this webhook can be added
 * or removed without prior notice"*. Um `.strict()` aqui — o padrão do resto de
 * `@baiocchi/shared`, por decisão A7 — transformaria "a Lais acrescentou um campo"
 * em lead pago descartado com `422`. Então:
 *
 *  - `.passthrough()`: campo desconhecido **entra** no objeto validado e é
 *    preservado no payload bruto do evento (`eventos_integracao.payload`);
 *  - nenhum campo opcional é `.strict()` no formato. Campo vazio (`""`) chega como
 *    `null` em vez de reprovar, porque a própria documentação diz que a Lais manda
 *    `"client_unit_id": ""` quando não tem o dado (§21 do payload de referência).
 *
 * ### 2. Os enums documentados são **listas de referência**, não restrições
 *
 * `ORIGENS_LAIS`, `GATILHOS_ENTREGA_LAIS`, `TIPOS_TRANSACAO_LAIS` e
 * `TIPOS_IMOVEL_LAIS` existem para rótulo de tela e para o teste que confere que a
 * documentação foi lida — **não** entram como `z.enum()` na validação. O motivo é o
 * mesmo do item 1, e a documentação da Lais lista dezenas de origens ("...", no
 * meio da lista). Uma `enum` fechada faria o primeiro portal novo virar `422`.
 *
 * O valor cru é preservado como texto. Ver a nota longa em `LaisLead` no
 * `schema.prisma`.
 *
 * ### 3. `origin` e `delivery_triggered_by` são coisas diferentes
 *
 * `origin` é **de onde o lead veio** (`GrupoZapLais`, `FacebookAdsLais`).
 * `delivery_triggered_by` é **por que a Lais mandou o lead agora**
 * (`qualified-by-lais`, `visit-scheduled`, `credit-analysis-completed`). O mesmo
 * lead chega mais de uma vez, com `origin` igual e `delivery_triggered_by`
 * diferente — confundir os dois apagaria a razão de cada entrega.
 */
import { z } from 'zod'

// ---------------------------------------------------------------------------
// Listas de referência (documentação, rótulo e teste — nunca validação)
// ---------------------------------------------------------------------------

/**
 * Origens documentadas pela Lais. **Lista aberta.**
 *
 * A documentação traz "..." no meio da enumeração, o que é a própria Lais dizendo
 * que a lista não está completa. Serve para rótulo e para o teste que prova que a
 * implementação não presume `GrupoZapLais`.
 */
export const ORIGENS_LAIS = [
  'WhatsAppLais',
  'GrupoZapLais',
  'ImovelWebLais',
  'CasaMineiraLais',
  'ChavesNaMaoLais',
  'DFImoveisLais',
  'Sub100Lais',
  'ImobListLais',
  'L4SLais',
  'CliqueiMudeiLais',
  '123ImoveisLais',
  'MappoLais',
  'WImoveisLais',
  'GuiaDeImoveisLais',
  'GuiaDeImoveisABCLais',
  'GuiaDeImoveisSPLais',
  'FacebookAdsLais',
  'InstagramAdsLais',
  'GoogleAdsLais',
  'ChatsLais',
  'PropWebsiteLais',
  'PropEmailLais',
  'ReachOutLais',
  'PlacaLais',
] as const

export type OrigemLaisConhecida = (typeof ORIGENS_LAIS)[number]

/**
 * Motivos de entrega documentados. **Lista aberta**, mesmo raciocínio.
 *
 * Cada um destes é uma entrega legítima do **mesmo** lead: um lead qualificado
 * (`qualified-by-lais`) que depois agenda visita (`visit-scheduled`) e depois tem
 * a análise de crédito concluída (`credit-analysis-completed`) chega três vezes.
 */
export const GATILHOS_ENTREGA_LAIS = [
  'qualified-by-lais',
  'qualified-by-client',
  'invalid-phone-number',
  'auto-sent',
  'visit-scheduled',
  'out-of-scope',
  'message-delivery-error',
  'credit-analysis-completed',
  'qualified-by-classification',
] as const

export type GatilhoEntregaLais = (typeof GATILHOS_ENTREGA_LAIS)[number]

export const ROTULO_GATILHO_ENTREGA_LAIS: Readonly<Record<GatilhoEntregaLais, string>> = {
  'qualified-by-lais': 'Qualificado pela Lais',
  'qualified-by-client': 'Qualificado pelo cliente',
  'invalid-phone-number': 'Telefone inválido',
  'auto-sent': 'Envio automático',
  'visit-scheduled': 'Visita agendada',
  'out-of-scope': 'Fora do escopo',
  'message-delivery-error': 'Erro na entrega da mensagem',
  'credit-analysis-completed': 'Análise de crédito concluída',
  'qualified-by-classification': 'Qualificado por classificação',
}

/** `buy` / `rent` / `pre-launch`. Lista aberta. */
export const TIPOS_TRANSACAO_LAIS = ['buy', 'rent', 'pre-launch'] as const

export type TipoTransacaoLais = (typeof TIPOS_TRANSACAO_LAIS)[number]

export const ROTULO_TIPO_TRANSACAO_LAIS: Readonly<Record<TipoTransacaoLais, string>> = {
  buy: 'Compra',
  rent: 'Aluguel',
  'pre-launch': 'Pré-lançamento',
}

/** Tipos de imóvel documentados. Lista aberta — §4 da missão pede não restringir. */
export const TIPOS_IMOVEL_LAIS = [
  'house',
  'loft',
  'apartment',
  'farm',
  'land',
  'commercial_property',
  'farmhouse',
  'hotel',
] as const

export type TipoImovelLais = (typeof TIPOS_IMOVEL_LAIS)[number]

export const ROTULO_TIPO_IMOVEL_LAIS: Readonly<Record<TipoImovelLais, string>> = {
  house: 'Casa',
  loft: 'Loft',
  apartment: 'Apartamento',
  farm: 'Fazenda',
  land: 'Terreno',
  commercial_property: 'Imóvel comercial',
  farmhouse: 'Chácara',
  hotel: 'Hotel',
}

// ---------------------------------------------------------------------------
// Primitivas de leitura tolerante
// ---------------------------------------------------------------------------

/**
 * Texto **obrigatório**: presente, não vazio depois de `trim`.
 *
 * O teto de 4000 caracteres não é estética — `conversation_summary` é texto livre
 * gerado por conversa, e sem teto uma resposta patológica viraria uma linha de
 * megabytes na timeline. O corte é registrado no relatório de leitura.
 */
const textoObrigatorio = (campo: string, maximo = 4_000) =>
  z.preprocess(
    (valor) => (typeof valor === 'string' ? valor.trim() : valor),
    z
      .string({ required_error: `${campo} é obrigatório.`, invalid_type_error: `${campo} precisa ser texto.` })
      .min(1, `${campo} é obrigatório.`)
      .max(maximo, `${campo} excede ${maximo} caracteres.`),
  )

/**
 * Texto **opcional** em que vazio significa ausente.
 *
 * `""` → `null`, e não erro: §19 da missão é literal ("não rejeitar payload válido
 * apenas porque email está vazio"), e o payload de referência da própria Lais traz
 * `"client_unit_id": ""`.
 */
const textoOpcional = (maximo = 2_000) =>
  z.preprocess(
    (valor) => {
      if (valor === null || valor === undefined) return null
      if (typeof valor === 'number' && Number.isFinite(valor)) return String(valor)
      if (typeof valor !== 'string') return null
      const limpo = valor.trim()
      return limpo.length === 0 ? null : limpo.slice(0, maximo)
    },
    z.string().max(maximo).nullable(),
  )

/**
 * Número opcional que **aceita string numérica**.
 *
 * A documentação declara `bedrooms` como `number`, mas integração de portal manda
 * `"2"` com frequência suficiente para isto não ser paranoia — e recusar o payload
 * inteiro por causa de um quarto em texto perderia o lead. Valor não numérico vira
 * `null` em vez de erro: o dado do imóvel é acessório, o lead não é.
 */
const numeroOpcional = (opcoes: { inteiro?: boolean; minimo?: number; maximo?: number } = {}) =>
  z.preprocess((valor) => {
    if (valor === null || valor === undefined || valor === '') return null

    // Só número e texto são convertidos. Objeto, array e booleano viram `null` em vez
    // de passar por `String()` — que produziria `"[object Object]"` e depois `NaN`, o
    // mesmo resultado por um caminho que esconde o motivo.
    let numero: number
    if (typeof valor === 'number') {
      numero = valor
    } else if (typeof valor === 'string') {
      // Vírgula decimal: `"90,5"` chega de integração de portal brasileiro.
      numero = Number(valor.replace(',', '.'))
    } else {
      return null
    }

    if (!Number.isFinite(numero)) return null
    if (opcoes.minimo !== undefined && numero < opcoes.minimo) return null
    if (opcoes.maximo !== undefined && numero > opcoes.maximo) return null
    return opcoes.inteiro === true ? Math.trunc(numero) : numero
  }, z.number().nullable())

/**
 * Instante ISO-8601 UTC → `Date`.
 *
 * `null` quando ilegível, para os campos opcionais. O obrigatório (`created_at`) usa
 * `instanteObrigatorio`, que reprova: sem a data de criação na Lais não há como
 * carimbar `chegada_em`, que é imutável por trigger no banco.
 */
export function instanteLais(valor: unknown): Date | null {
  if (valor instanceof Date) return Number.isNaN(valor.getTime()) ? null : valor
  if (typeof valor === 'number' && Number.isFinite(valor)) {
    const deNumero = new Date(valor > 1e12 ? valor : valor * 1000)
    return Number.isNaN(deNumero.getTime()) ? null : deNumero
  }
  if (typeof valor !== 'string') return null
  const limpo = valor.trim()
  if (limpo.length === 0) return null
  const data = new Date(limpo)
  return Number.isNaN(data.getTime()) ? null : data
}

const instanteOpcional = z.preprocess((valor) => instanteLais(valor), z.date().nullable())

const instanteObrigatorio = (campo: string) =>
  z.preprocess(
    (valor) => instanteLais(valor) ?? undefined,
    z.date({
      required_error: `${campo} é obrigatório.`,
      invalid_type_error: `${campo} precisa ser um timestamp ISO-8601 em UTC.`,
    }),
  )

/**
 * Lista de instantes, **ordenada e sem repetição**.
 *
 * §5 da missão: "não descartar o array em favor do campo singular". Itens ilegíveis
 * caem fora silenciosamente em vez de reprovar a lista inteira — perder uma opção
 * de horário mal formatada é aceitável; perder o lead por causa dela não é.
 *
 * A ordenação é aplicada aqui e não confiada ao remetente: a documentação promete
 * ordem cronológica crescente, e uma promessa do outro lado não é uma garantia.
 */
const listaDeInstantes = z.preprocess((valor) => {
  if (!Array.isArray(valor)) return []
  const datas = valor
    .map((item) => instanteLais(item))
    .filter((item): item is Date => item !== null)
    .slice(0, 50)
  const unicos = new Map<number, Date>()
  for (const data of datas) unicos.set(data.getTime(), data)
  return [...unicos.values()].sort((a, b) => a.getTime() - b.getTime())
}, z.array(z.date()))

// ---------------------------------------------------------------------------
// credit_analysis
// ---------------------------------------------------------------------------

/**
 * Análise de crédito — **objeto opcional que pode chegar vazio**.
 *
 * A documentação da Lais explica os três estados em que o objeto não vem completo:
 * a análise ainda não rodou, está rodando, ou falhou. E diz que o resultado pode
 * ser enviado **depois**, numa nova entrega do mesmo lead com
 * `delivery_triggered_by = credit-analysis-completed`.
 *
 * Consequência direta no schema: **todo** campo é opcional, inclusive `status`.
 * Um `credit_analysis: {}` é válido e significa "a análise existe como conceito,
 * ainda não como resultado".
 */
export const schemaAnaliseCreditoLais = z
  .object({
    provider: textoOpcional(120),
    amount: numeroOpcional({ minimo: 0, maximo: 1e12 }),
    status: textoOpcional(80),
    client_listing_id: textoOpcional(120),
    analysis_type: textoOpcional(80),
    /** CPF/CNPJ do titular. **Dado sensível**: mascarado em log, nunca em resposta. */
    vat_number: textoOpcional(40),
    provider_analysis_id: textoOpcional(160),
    reason: textoOpcional(1_000),
    chat_id: textoOpcional(80),
  })
  .passthrough()

export type AnaliseCreditoLais = z.infer<typeof schemaAnaliseCreditoLais>

// ---------------------------------------------------------------------------
// Payload do webhook
// ---------------------------------------------------------------------------

/**
 * `POST /integracoes/lais/qualified-lead`.
 *
 * Os nomes dos campos são os da Lais (`snake_case`, inglês) e ficam assim de
 * propósito: este objeto é o contrato de outra empresa. A tradução para o
 * vocabulário do CRM acontece em `mapeador-lais.ts`, num lugar só.
 *
 * ## Obrigatórios (§19 da missão)
 *
 * `id`, `origin`, `name`, `number`, `created_at`, `conversation_summary`,
 * `conversation_history`, `transaction_type`, `delivery_triggered_by`. Nove campos,
 * exatamente os nove que a missão manda validar. Todo o resto é opcional e a
 * ausência **não** é erro.
 *
 * ## Por que `id` não é validado como UUID
 *
 * A documentação diz "string UUID", e hoje é. Mas o campo é a chave de identidade
 * de toda a integração, e um `z.string().uuid()` faz o CRM recusar o lead se a Lais
 * mudar o formato do identificador — que é exatamente o tipo de mudança que a
 * documentação avisa que acontece sem aviso. Validamos o que precisamos garantir:
 * que existe, que é texto e que cabe na coluna.
 */
export const schemaWebhookLaisQualifiedLead = z
  .object({
    // --- identidade e obrigatórios -----------------------------------------
    id: textoObrigatorio('id', 200),
    origin: textoObrigatorio('origin', 120),
    name: textoObrigatorio('name', 200),
    number: textoObrigatorio('number', 40),
    created_at: instanteObrigatorio('created_at'),
    conversation_summary: textoObrigatorio('conversation_summary', 8_000),
    conversation_history: textoObrigatorio('conversation_history', 2_000),
    transaction_type: textoObrigatorio('transaction_type', 40),
    delivery_triggered_by: textoObrigatorio('delivery_triggered_by', 80),

    // --- contato opcional ---------------------------------------------------
    email: textoOpcional(320),

    // --- imóvel -------------------------------------------------------------
    link: textoOpcional(2_000),
    portal_listing_id: textoOpcional(120),
    client_listing_id: textoOpcional(120),
    client_unit_id: textoOpcional(120),
    type: textoOpcional(80),
    area: numeroOpcional({ minimo: 0, maximo: 1e9 }),
    bedrooms: numeroOpcional({ inteiro: true, minimo: 0, maximo: 99 }),
    bathrooms: numeroOpcional({ inteiro: true, minimo: 0, maximo: 99 }),
    parking_spaces: numeroOpcional({ inteiro: true, minimo: 0, maximo: 99 }),
    price_rent: numeroOpcional({ minimo: 0, maximo: 1e12 }),
    price_buy: numeroOpcional({ minimo: 0, maximo: 1e12 }),
    neighborhood: textoOpcional(160),
    city: textoOpcional(160),

    // --- visitas (§5) -------------------------------------------------------
    visit_request_date_time: instanteOpcional,
    visit_request_date_time_options: listaDeInstantes.default([]),

    // --- Google (§6) --------------------------------------------------------
    google_utm_medium: textoOpcional(200),
    google_utm_source: textoOpcional(200),
    google_external_id: textoOpcional(200),

    // --- Facebook / Meta (§7) ----------------------------------------------
    facebook_utm_medium: textoOpcional(200),
    facebook_utm_source: textoOpcional(200),
    facebook_utm_campaign: textoOpcional(300),
    facebook_utm_campaign_id: textoOpcional(120),
    facebook_utm_ad: textoOpcional(300),
    facebook_utm_ad_id: textoOpcional(120),
    facebook_utm_adset: textoOpcional(300),
    facebook_utm_adset_id: textoOpcional(120),
    facebook_utm_form_name: textoOpcional(300),
    facebook_utm_platform: textoOpcional(120),
    facebook_utm_account_id: textoOpcional(120),

    // --- Facebook referral / CTWA (§8) -------------------------------------
    facebook_referral_ctwa_clid: textoOpcional(400),
    facebook_referral_headline: textoOpcional(400),
    facebook_referral_source_id: textoOpcional(160),
    facebook_referral_source_url: textoOpcional(2_000),
    facebook_referral_source_type: textoOpcional(120),

    // --- análise de crédito (§9) -------------------------------------------
    credit_analysis: schemaAnaliseCreditoLais.nullish(),
  })
  /**
   * `passthrough` e não `strict`: campo novo da Lais entra no objeto e segue para o
   * payload bruto do evento. É a exigência de §20 da missão, e o oposto do padrão
   * do resto do pacote — ver o cabeçalho deste arquivo.
   */
  .passthrough()

export type WebhookLaisQualifiedLead = z.infer<typeof schemaWebhookLaisQualifiedLead>

// ---------------------------------------------------------------------------
// Resposta — o contrato que a Lais espera
// ---------------------------------------------------------------------------

/**
 * Sucesso. **A Lais considera entregue somente com `HTTP 201`** — qualquer outro
 * status, 200 incluído, é falha do ponto de vista dela. Ver §2 da missão.
 */
export interface RespostaSucessoLais {
  readonly success: true
  readonly message: string
}

/**
 * Erro. Envelope próprio, diferente do `{ erro: { codigo, mensagem } }` do resto da
 * API — porque quem consome esta rota é a Lais, e o formato é o dela.
 *
 * `errors` é o detalhamento por campo, no formato que a documentação da Lais
 * exemplifica (`{ "id": ["error 1", "error 2"] }`). Nunca carrega stack, nome de
 * tabela, nem o motivo interno de uma recusa de autenticação.
 */
export interface RespostaErroLais {
  readonly success: false
  readonly message: string
  readonly errors?: Readonly<Record<string, ReadonlyArray<string>>>
}

export type RespostaLais = RespostaSucessoLais | RespostaErroLais

/** Mensagem única de sucesso — a Lais só lê `success`, mas o log humano lê isto. */
export const MENSAGEM_SUCESSO_LAIS = 'Lead received successfully'

/**
 * Caminho da rota, **sem** o prefixo `/api/v1`.
 *
 * Mora aqui, e não só no `@Controller`, porque duas camadas precisam reconhecer a rota
 * e não podem depender uma da outra: o controller (que a declara) e o filtro global de
 * exceção, em `comum/erros`, que precisa devolver o envelope da Lais nos erros que o
 * Fastify levanta **antes** de a rota ser resolvida — corpo que não é JSON, corpo
 * acima do limite. Nesses casos não existe controller, então o filtro de controller
 * não é alcançado, e sem esta constante o parceiro receberia o envelope interno do CRM.
 */
export const ROTA_WEBHOOK_LAIS = 'integracoes/lais/qualified-lead'

/** `true` quando a URL da requisição é o webhook da Lais (com ou sem query string). */
export function ehRotaWebhookLais(url: string | undefined): boolean {
  if (url === undefined) return false
  const caminho = url.split('?')[0] ?? ''
  return caminho.endsWith(`/${ROTA_WEBHOOK_LAIS}`)
}

/**
 * Envelope de erro da Lais para um status HTTP, **sem** detalhe interno.
 *
 * Fonte única das mensagens: usada pelo filtro da rota e pelo filtro global, para que
 * um erro pré-roteamento e um erro de handler não descrevam o mesmo `415` de duas
 * formas diferentes.
 */
export function respostaErroLaisPorHttp(http: number): RespostaErroLais {
  return { success: false, message: mensagemErroLaisPorHttp(http) }
}

export function mensagemErroLaisPorHttp(http: number): string {
  switch (http) {
    case 400:
      return 'Malformed request body: expected valid JSON.'
    case 401:
    case 403:
      return 'Unauthorized.'
    case 404:
      return 'Endpoint not found.'
    case 405:
      return 'Method not allowed: use POST.'
    case 413:
      return 'Payload too large.'
    case 415:
      return 'Unsupported Content-Type: use application/json.'
    case 429:
      return 'Too many requests. Please retry later.'
    default:
      return http >= 500
        ? 'Internal error while processing the lead. Please retry.'
        : 'Invalid request.'
  }
}

/**
 * Desfecho de uma entrega, do ponto de vista do CRM. Não vai para a Lais (ela só
 * quer 201) — vai para o log, para a timeline e para o teste.
 */
export const DESFECHOS_LAIS = [
  /** Primeira entrega deste `id`: cliente e lead criados. */
  'LEAD_CRIADO',
  /**
   * `id` novo, mas o cliente já tinha atendimento aberto (RN-10). O payload entrou
   * na timeline do lead existente em vez de criar um segundo atendimento.
   */
  'ANEXADO_A_LEAD_EXISTENTE',
  /** `id` conhecido, conteúdo novo: lead atualizado e evento registrado na timeline. */
  'LEAD_ATUALIZADO',
  /** Reentrega byte a byte idêntica: nada mudou, nada foi duplicado. */
  'ENTREGA_REPETIDA',
  /**
   * Entrega aceita e gravada, **lead não criado** — o telefone não normaliza para
   * E.164, que é a chave de deduplicação de cliente (RN-10).
   *
   * É o caso do `delivery_triggered_by = invalid-phone-number`, que a Lais envia de
   * propósito. A resposta continua `201` porque a entrega foi bem-sucedida: o payload
   * está preservado, o administrador é notificado e a tela de eventos permite
   * reprocessar. Devolver erro faria a Lais reentregar em looping algo que nunca vai
   * passar. Ver a nota de `registrarEntregaSemLead` em `lais.service.ts`.
   */
  'ENTREGA_SEM_LEAD',
] as const

export type DesfechoLais = (typeof DESFECHOS_LAIS)[number]

export const ROTULO_DESFECHO_LAIS: Readonly<Record<DesfechoLais, string>> = {
  LEAD_CRIADO: 'Lead criado',
  ANEXADO_A_LEAD_EXISTENTE: 'Anexado ao atendimento existente',
  LEAD_ATUALIZADO: 'Lead atualizado',
  ENTREGA_REPETIDA: 'Entrega repetida (idempotente)',
  ENTREGA_SEM_LEAD: 'Entrega registrada sem criar lead (telefone inválido)',
}
